import { useState, useEffect, useContext, useCallback } from "react";
import { sessionManager } from "@authentication/services/SessionManager";
import { FeedbackService } from "@api/feedback/feedback";
import { authenticationService } from "@authentication/services/AuthenticationService";
import FeedbackContext from "../FeedbackContext";

export const useFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [userReady, setUserReady] = useState(false);
  const [isConnected, setIsConnected] = useState(null);
  const { setFeedbackCount } = useContext(FeedbackContext);

  const getUserInfo = useCallback(() => {
    try {
      const user = sessionManager.user || {};
      const userName = user.email || user.fullName || user.id || "Unknown User";
      const roles = Array.isArray(user.roles)
        ? user.roles.map((r) => r.toLowerCase())
        : user.roles
        ? [user.roles.toLowerCase()]
        : [];
      const isAllowedToEdit =
        roles.includes("admin") || roles.includes("librarian");

      return { user, userName, roles, isAllowedToEdit };
    } catch (error) {
      return {
        user: {},
        userName: "Unknown User",
        roles: [],
        isAllowedToEdit: false,
      };
    }
  }, []);

  const loadFeedbacks = useCallback(async () => {
    if (!userReady) return;

    setIsLoading(true);
    setError("");

    try {
      const { userName, isAllowedToEdit } = getUserInfo();
      const directResponse = await authenticationService._makeRequest(
        "get",
        "/feedback/messages"
      );

      let fetchedFeedbacks = [];
      if (directResponse?.data) {
        if (Array.isArray(directResponse.data)) {
          fetchedFeedbacks = directResponse.data;
        } else if (
          directResponse.data.data &&
          Array.isArray(directResponse.data.data)
        ) {
          fetchedFeedbacks = directResponse.data.data;
        } else if (
          directResponse.data.hits &&
          Array.isArray(directResponse.data.hits)
        ) {
          fetchedFeedbacks = directResponse.data.hits;
        }
      }

      const validFeedbacks = fetchedFeedbacks
        .filter(
          (feedback) => feedback && (feedback.message || feedback.content)
        )
        .map((feedback) => ({
          ...feedback,
          id: feedback.id || feedback._id,
          message: feedback.message || feedback.content,
          category: feedback.category || "General Feedback",
          created_at:
            feedback.created_at ||
            feedback.createdAt ||
            new Date().toISOString(),
          created_by: feedback.created_by || feedback.createdBy,
          is_read: feedback.is_read || false,
        }));

      let filteredFeedbacks = validFeedbacks;
      if (!isAllowedToEdit) {
        filteredFeedbacks = validFeedbacks.filter((feedback) => {
          const isOwnFeedback = feedback.created_by === userName;
          return isOwnFeedback && !feedback.is_read;
        });
      }

      setFeedbacks(filteredFeedbacks);
      setFeedbackCount(filteredFeedbacks.length);
    } catch (error) {
      setError(`Failed to load feedbacks: ${error.message}`);
      setFeedbacks([]);
      setFeedbackCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [userReady, getUserInfo, setFeedbackCount]);

  useEffect(() => {
    const checkUserReady = () => {
      const { user } = getUserInfo();
      if (user && (user.email || user.fullName || user.id)) {
        setUserReady(true);
        FeedbackService.setCurrentUser({
          id: user.id,
          email: user.email,
          fullName: user.fullName,
          role: user.roles,
        });
      } else {
        const timer = setTimeout(checkUserReady, 100);
        return () => clearTimeout(timer);
      }
    };
    checkUserReady();
  }, [getUserInfo]);

  useEffect(() => {
    if (userReady) {
      setIsConnected(true);
      loadFeedbacks();
    }
  }, [userReady, loadFeedbacks]);

  return {
    feedbacks,
    isLoading,
    error,
    successMessage,
    userReady,
    isConnected,
    setError,
    setSuccessMessage,
    loadFeedbacks,
    getUserInfo,
  };
};
